import { EVIDENCE_STATUSES, RISK_LEVELS } from "./NinaScannerSchema";
import {
  MARKET_DECIMAL_PLACES,
  RVOL_DECIMAL_PLACES,
  canonicalDecimal,
  deriveMarketFields,
} from "./NinaScannerValidation";
import {
  OPTION_EVIDENCE_STATUSES,
  OPTION_RISK_LEVELS,
  OPTION_TYPES,
} from "../optionsScanner/NinaOptionsScannerSchema";

export const SCANNER_REVIEW_VERSION = 1;

const DAY_DIRECTIONS = ["Bullish bias", "Bearish bias", "Neutral bias"];
const OPTION_DIRECTIONS = ["bullish", "bearish"];
const COMMON_KEYS = [
  "version",
  "kind",
  "symbol",
  "setup",
  "direction",
  "riskLevel",
  "smartFlow",
  "confidence",
  "evidenceStatus",
  "evidenceCompleteness",
  "source",
  "asOf",
  "simulated",
];
const OPTION_KEYS = [
  ...COMMON_KEYS,
  "contractId",
  "contractType",
  "expiration",
  "dte",
  "strike",
  "bid",
  "ask",
  "spread",
  "contractMultiplier",
  "premium",
  "maxLoss",
];
const AUTHENTICATED_OPTION_KEYS = [
  ...OPTION_KEYS,
    "openInterest",
  "openInterestDate",
  "dataMode",
  "qualification",
  "executable",
];
const AUTHENTICATED_OPTION_DATA_MODE = "authenticated-indicative";
const DAY_MARKET_KEYS = [
  "lastPrice",
  "previousClose",
  "openPrice",
  "bid",
  "ask",
  "spread",
  "dayLow",
  "dayHigh",
  "volume",
  "relativeVolume",
  "updatedAt",
];
const DAY_KEYS = [...COMMON_KEYS, ...DAY_MARKET_KEYS];
const SYMBOL_REVIEW_KINDS = [
  "premarket",
  "swing",
  "options-engine",
  "opportunity-center",
];
const SYMBOL_REVIEW_KEYS = COMMON_KEYS;
const MAX_MARKET_PRICE = 1_000_000;
const MAX_RELATIVE_VOLUME = 1_000;
const DECIMAL_EPSILON = 1e-9;

const fail = (message) => ({
  valid: false,
  value: null,
  error: `Scanner review could not be opened: ${message}`,
});

const isPlainObject = (value) =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

const finite = (value) => typeof value === "number" && Number.isFinite(value);
const nonEmpty = (value) =>
  typeof value === "string" && value.trim().length > 0;
const validTimestamp = (value) =>
  nonEmpty(value) &&
  Number.isFinite(Date.parse(value)) &&
  new Date(value).toISOString() === value;
const validSymbol = (value) =>
  typeof value === "string" && /^[A-Z][A-Z0-9.-]{0,9}$/.test(value);
const round4 = (value) => Math.round((value + Number.EPSILON) * 10000) / 10000;
const canonical = (value, places) => canonicalDecimal(value, places);
const canonicalMatches = (value, places) =>
  Math.abs(value - canonical(value, places)) <= DECIMAL_EPSILON;

function validateExactKeys(payload, allowedKeys) {
  const keys = Object.keys(payload);
  const unexpected = keys.find((key) => !allowedKeys.includes(key));
  if (unexpected) return `unexpected field "${unexpected}"`;
  const missing = allowedKeys.find(
    (key) => !Object.prototype.hasOwnProperty.call(payload, key),
  );
  return missing ? `missing required field "${missing}"` : "";
}

function validateCommon(
  payload,
  riskLevels,
  evidenceStatuses,
  directions,
  allowUnavailableScores = false,
) {
  if (payload.version !== SCANNER_REVIEW_VERSION)
    return "unsupported handoff version";
  if (!validSymbol(payload.symbol)) return "invalid symbol";
  if (!nonEmpty(payload.setup)) return "setup must be a non-empty string";
  if (!directions.includes(payload.direction)) return "invalid direction";
  if (!riskLevels.includes(payload.riskLevel)) return "invalid risk level";
  if (
    !(allowUnavailableScores && payload.smartFlow === null) &&
    (!finite(payload.smartFlow) ||
      payload.smartFlow < 0 ||
      payload.smartFlow > 100)
  ) {
    return "SmartFlow must be a finite number from 0 to 100";
  }
  if (
    !(allowUnavailableScores && payload.confidence === null) &&
    (!finite(payload.confidence) ||
      payload.confidence < 0 ||
      payload.confidence > 100)
  ) {
    return "Confidence must be a finite number from 0 to 100";
  }
  if (
    !(allowUnavailableScores && payload.evidenceCompleteness === null) &&
    (!finite(payload.evidenceCompleteness) ||
      payload.evidenceCompleteness < 0 ||
      payload.evidenceCompleteness > 100)
  )
    return "evidence completeness must be a finite number from 0 to 100";
  if (!evidenceStatuses.includes(payload.evidenceStatus))
    return "invalid evidence status";
  if (!nonEmpty(payload.source)) return "source must be a non-empty string";
  if (!validTimestamp(payload.asOf)) return "invalid as-of timestamp";
  if (payload.simulated !== true)
    return "only simulated Scanner reviews are supported";
  return "";
}

export function validateScannerReviewHandoff(payload) {
  if (!isPlainObject(payload)) return fail("handoff must be an object");
  if (
    payload.kind !== "day" &&
    payload.kind !== "options" &&
    !SYMBOL_REVIEW_KINDS.includes(payload.kind)
  ) {
    return fail("invalid handoff type");
  }
  const isAuthenticatedOption =
    payload.kind === "options" &&
    payload.dataMode === AUTHENTICATED_OPTION_DATA_MODE;
  const allowedKeys = isAuthenticatedOption
    ? AUTHENTICATED_OPTION_KEYS
    : payload.kind === "options"
      ? OPTION_KEYS
      : payload.kind === "day"
        ? DAY_KEYS
        : SYMBOL_REVIEW_KEYS;
  const keyError = validateExactKeys(payload, allowedKeys);
  if (keyError) return fail(keyError);

  const commonError = validateCommon(
    payload,
    payload.kind === "options" ? OPTION_RISK_LEVELS : RISK_LEVELS,
    payload.kind === "options" ? OPTION_EVIDENCE_STATUSES : EVIDENCE_STATUSES,
    payload.kind === "options"
      ? isAuthenticatedOption
        ? [...OPTION_DIRECTIONS, "unconfirmed"]
        : OPTION_DIRECTIONS
      : DAY_DIRECTIONS,
    isAuthenticatedOption,
  );
  if (commonError) return fail(commonError);

  if (payload.kind === "options") {
    if (
      isAuthenticatedOption &&
      !/^[A-Z][A-Z0-9.-]{0,9}\d{6}[CP]\d{8}$/.test(payload.contractId)
    ) {
      return fail("invalid authenticated option contract identity");
    }
    if (
      !isAuthenticatedOption &&
      !/^SIM-[A-Z0-9-]+$/.test(payload.contractId)
    ) {
      return fail("invalid simulated contract identity");
    }
    if (!OPTION_TYPES.includes(payload.contractType))
      return fail("invalid contract type");
    if (
      payload.direction !== "unconfirmed" &&
      ((payload.contractType === "call" && payload.direction !== "bullish") ||
        (payload.contractType === "put" && payload.direction !== "bearish"))
    )
      return fail("contract type and direction are inconsistent");
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(payload.expiration) ||
      !Number.isFinite(Date.parse(`${payload.expiration}T00:00:00.000Z`))
    ) {
      return fail("invalid expiration");
    }
    if (!Number.isInteger(payload.dte) || payload.dte < 0)
      return fail("invalid DTE");
    const expirationDay = Date.parse(`${payload.expiration}T00:00:00.000Z`);
    const asOfDay = Date.parse(`${payload.asOf.slice(0, 10)}T00:00:00.000Z`);
    if (
      !isAuthenticatedOption &&
      (expirationDay - asOfDay) / 86400000 !== payload.dte
    ) {
      return fail("expiration, DTE, and as-of timestamp are inconsistent");
    }
    if (isAuthenticatedOption) {
      if (payload.qualification !== "WAIT") {
        return fail("authenticated indicative options must remain WAIT");
      }
      if (payload.executable !== false) {
        return fail("authenticated indicative options must be non-executable");
      }
      if (payload.evidenceStatus !== "unavailable") {
        return fail(
          "authenticated indicative evidence must remain unavailable",
        );
      }
      for (const key of ["smartFlow", "confidence", "evidenceCompleteness"]) {
        if (payload[key] !== null)
          return fail(`${key} must remain unavailable`);
      }
      for (const key of ["strike", "bid", "ask", "spread"]) {
        if (payload[key] === null) continue;
        if (!finite(payload[key]) || payload[key] < 0) {
          return fail(
            `${key} must be unavailable or a nonnegative finite number`,
          );
        }
        if (round4(payload[key]) !== payload[key]) {
          return fail(`${key} exceeds four-decimal precision`);
        }
      }
      if (payload.strike !== null && payload.strike <= 0) {
        return fail("strike must be positive when available");
      }
      if (payload.ask !== null && payload.ask <= 0) {
        return fail("ask must be positive when available");
      }
      if (
        payload.bid !== null &&
        payload.ask !== null &&
        payload.bid > payload.ask
      ) {
        return fail("bid cannot exceed ask");
      }
      if (
        payload.bid !== null &&
        payload.ask !== null &&
        payload.spread !== null &&
        round4(payload.ask - payload.bid) !== round4(payload.spread)
      ) {
        return fail("spread does not match ask minus bid");
      }
      if (
        payload.contractMultiplier !== null &&
        (!Number.isInteger(payload.contractMultiplier) ||
          payload.contractMultiplier <= 0)
      ) {
        return fail(
          "contract multiplier must be unavailable or a positive integer",
        );
      }
      if (
        payload.openInterest !== null &&
        (!Number.isInteger(payload.openInterest) || payload.openInterest < 0)
      ) {
        return fail(
          "open interest must be unavailable or a nonnegative integer",
        );
      }
      if (
        payload.openInterestDate !== null &&
        (typeof payload.openInterestDate !== "string" ||
          !/^\d{4}-\d{2}-\d{2}$/.test(payload.openInterestDate) ||
          Number.isNaN(Date.parse(`${payload.openInterestDate}T00:00:00Z`)))
      ) {
        return fail(
          "open interest date must be unavailable or a valid ISO date",
        );
      }
      if (
        payload.openInterest === null &&
        payload.openInterestDate !== null
      ) {
        return fail(
          "open interest date requires verified open interest",
        );
      }
      for (const key of ["premium", "maxLoss"]) {
        if (payload[key] !== null)
          return fail(`${key} must remain unavailable`);
      }
      return {
        valid: true,
        error: "",
        value: Object.freeze({ ...payload }),
      };
    }
    for (const key of [
      "strike",
      "bid",
      "ask",
      "spread",
      "premium",
      "maxLoss",
    ]) {
      if (!finite(payload[key]) || payload[key] < 0)
        return fail(`${key} must be a nonnegative finite number`);
      if (round4(payload[key]) !== payload[key])
        return fail(`${key} exceeds four-decimal precision`);
    }
    if (payload.strike <= 0 || payload.ask <= 0)
      return fail("strike and ask must be positive");
    if (
      !Number.isInteger(payload.contractMultiplier) ||
      payload.contractMultiplier <= 0
    ) {
      return fail("contract multiplier must be a positive integer");
    }
    if (payload.bid > payload.ask) return fail("bid cannot exceed ask");
    if (round4(payload.ask - payload.bid) !== round4(payload.spread)) {
      return fail("spread does not match ask minus bid");
    }
    if (
      round4(payload.ask * payload.contractMultiplier) !==
      round4(payload.premium)
    ) {
      return fail("premium does not match ask times multiplier");
    }
    if (round4(payload.premium) !== round4(payload.maxLoss)) {
      return fail(
        "maximum loss does not match the validated long-debit premium",
      );
    }
  } else if (payload.kind === "day") {
    for (const key of [
      "lastPrice",
      "previousClose",
      "openPrice",
      "bid",
      "ask",
      "dayLow",
      "dayHigh",
    ]) {
      if (
        !finite(payload[key]) ||
        payload[key] <= 0 ||
        payload[key] > MAX_MARKET_PRICE
      ) {
        return fail(`${key} must be a positive finite supported market price`);
      }
      if (!canonicalMatches(payload[key], MARKET_DECIMAL_PLACES)) {
        return fail(`${key} exceeds four-decimal precision`);
      }
    }
    if (
      !finite(payload.spread) ||
      payload.spread < 0 ||
      payload.spread > MAX_MARKET_PRICE ||
      !canonicalMatches(payload.spread, MARKET_DECIMAL_PLACES)
    ) {
      return fail(
        "spread must be a nonnegative finite value with four-decimal precision",
      );
    }
    if (!Number.isSafeInteger(payload.volume) || payload.volume < 0) {
      return fail("volume must be a nonnegative safe integer");
    }
    if (
      !finite(payload.relativeVolume) ||
      payload.relativeVolume < 0 ||
      payload.relativeVolume > MAX_RELATIVE_VOLUME ||
      !canonicalMatches(payload.relativeVolume, RVOL_DECIMAL_PLACES)
    ) {
      return fail(
        "relative volume must be a supported nonnegative finite value",
      );
    }
    if (!validTimestamp(payload.updatedAt))
      return fail("invalid updated timestamp");
    if (payload.bid > payload.ask) return fail("bid cannot exceed ask");
    if (payload.dayLow > payload.dayHigh)
      return fail("day low cannot exceed day high");
    if (
      payload.lastPrice < payload.dayLow ||
      payload.lastPrice > payload.dayHigh
    ) {
      return fail("last price must be within the day range");
    }
    if (
      payload.openPrice < payload.dayLow ||
      payload.openPrice > payload.dayHigh
    ) {
      return fail("open price must be within the day range");
    }
    const derived = deriveMarketFields(payload);
    if (Math.abs(payload.spread - derived.spread) > DECIMAL_EPSILON) {
      return fail("spread does not match ask minus bid");
    }
  }

  return {
    valid: true,
    error: "",
    value: Object.freeze({ ...payload }),
  };
}

export function createDayReviewHandoff(result) {
  return validateScannerReviewHandoff({
    version: SCANNER_REVIEW_VERSION,
    kind: "day",
    symbol: result.symbol,
    setup: result.setup,
    direction: result.direction,
    riskLevel: result.riskLevel,
    smartFlow: result.smartFlow.score,
    confidence: result.confidence.score,
    evidenceStatus: result.evidenceStatus,
    evidenceCompleteness: result.smartFlow.completeness,
    source: result.source,
    asOf: result.latestAsOf,
    simulated: true,
    lastPrice: result.lastPrice,
    previousClose: result.previousClose,
    openPrice: result.openPrice,
    bid: result.bid,
    ask: result.ask,
    spread: result.spread,
    dayLow: result.dayLow,
    dayHigh: result.dayHigh,
    volume: result.volume,
    relativeVolume: result.relativeVolume,
    updatedAt: result.updatedAt,
  });
}

export function createOptionsReviewHandoff(result) {
  const handoff = {
    version: SCANNER_REVIEW_VERSION,
    kind: "options",
    symbol: result.symbol,
    setup: result.setup,
    direction: result.direction,
    riskLevel: result.riskLevel,
    smartFlow: result.scores.smartFlow,
    confidence: result.scores.confidence,
    evidenceStatus: result.evidenceStatus,
    evidenceCompleteness: result.scores.completeness,
    source: result.source,
    asOf: result.asOf,
    simulated: true,
    contractId: result.contractId,
    contractType: result.contractType,
    expiration: result.expiration,
    dte: result.dte,
    strike: result.strike,
    bid: result.bid,
    ask: result.ask,
    spread: result.spread,
    contractMultiplier: result.contractMultiplier ?? null,
    premium: result.premium,
    maxLoss: result.maxLoss,
  };
  if (result.dataMode === AUTHENTICATED_OPTION_DATA_MODE) {
    return validateScannerReviewHandoff({
      ...handoff,
            openInterest: result.openInterest ?? null,
      openInterestDate: result.openInterestDate ?? null,
      dataMode: AUTHENTICATED_OPTION_DATA_MODE,
      qualification: result.qualification,
      executable: result.executable,
    });
  }
  return validateScannerReviewHandoff(handoff);
}

export function createPremarketReviewHandoff(result) {
  return createSymbolReviewHandoff({
    kind: "premarket",
    symbol: result.symbol,
    setup: result.summary,
    direction: "Bullish bias",
    riskLevel: ["A+", "A", "A-", "B+", "B"].includes(result.setupGrade)
      ? "Moderate"
      : "High",
    smartFlow: result.smartFlowConfirmed ? 100 : 25,
    confidence: result.confidence,
    evidenceCompleteness: 100,
    source: result.source,
    asOf: result.asOf,
  });
}

export function createSwingReviewHandoff(result) {
  return createSymbolReviewHandoff({
    kind: "swing",
    symbol: result.symbol,
    setup: result.setup,
    direction: String(result.direction).toLowerCase().includes("bear")
      ? "Bearish bias"
      : "Bullish bias",
    riskLevel: result.riskReward >= 2 ? "Moderate" : "High",
    smartFlow: result.technicalScore,
    confidence: result.confidence,
    evidenceCompleteness: result.evidenceCompleteness,
    source: result.source,
    asOf: result.asOf,
  });
}

export function createOptionsOpportunityReviewHandoff(result) {
  return createSymbolReviewHandoff({
    kind: "options-engine",
    symbol: result.symbol,
    setup: `${result.strategy}: ${result.setup}`,
    direction: result.direction === "Bearish" ? "Bearish bias" : "Bullish bias",
    riskLevel: result.riskLevel,
    smartFlow: result.opportunityScore,
    confidence: result.confidence,
    evidenceCompleteness: result.evidenceCompleteness,
    source: result.source,
    asOf: result.asOf,
  });
}

export function createAIOpportunityReviewHandoff(opportunity) {
  return createSymbolReviewHandoff({
    kind: "opportunity-center",
    symbol: opportunity.symbol,
    setup: `${opportunity.tradeType}: ${opportunity.status}`,
    direction: opportunity.direction,
    riskLevel: opportunity.riskLevel,
    smartFlow: opportunity.opportunityScore,
    confidence: opportunity.confidence,
    evidenceCompleteness: 100,
    source: `${opportunity.source} · AI Opportunity Center simulated consolidation`,
    asOf: opportunity.asOf,
  });
}

function createSymbolReviewHandoff(review) {
  return validateScannerReviewHandoff({
    version: SCANNER_REVIEW_VERSION,
    ...review,
    evidenceStatus: "simulated",
    asOf: new Date(review.asOf).toISOString(),
    simulated: true,
  });
}

export function openScannerReviewHandoff(handoff, onOpenTradeReview) {
  if (!handoff.valid) {
    return { ok: false, message: handoff.error };
  }
  const validation = validateScannerReviewHandoff(handoff.value);
  if (!validation.valid) {
    return { ok: false, message: validation.error };
  }
  const response = onOpenTradeReview?.(validation.value);
  if (!response) {
    return {
      ok: false,
      message: "Live Trading review navigation is unavailable.",
    };
  }
  return {
    ok: response.ok !== false,
    message: response.error || response.warning || "",
  };
}
